import { useState, useCallback, useRef, useEffect } from 'react';
import { apiRequest } from '../utils/api';

/**
 * Custom hook for handling async polling for agent tasks
 * Manages polling state, progress updates, and task completion
 */
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

const useAsyncPolling = ({ messages, setMessages, onTaskComplete }) => {
  const [isPolling, setIsPolling] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [currentProgressMessage, setCurrentProgressMessage] = useState('');
  const pollingIntervalRef = useRef(null);
  const consecutiveErrorsRef = useRef(0);

  // Polling utility function
  const pollTaskStatus = useCallback(async (taskId) => {
    const response = await apiRequest(`/api/agent/task-status/${taskId}/`);
    if (response.ok) {
      return await response.json();
    }
    throw new Error('Failed to fetch task status');
  }, []);

  // Start polling for task completion
  const startPolling = useCallback((taskId) => {
    console.log('🚀 [useAsyncPolling] Starting polling for task:', taskId);
    
    setIsPolling(true);
    setCurrentTaskId(taskId);
    setCurrentProgress(5);
    setCurrentProgressMessage('Starting agent analysis...');
    consecutiveErrorsRef.current = 0;
    
    const interval = setInterval(async () => {
      try {
        const statusData = await pollTaskStatus(taskId);
        consecutiveErrorsRef.current = 0;
        
        // Update progress
        setCurrentProgress(statusData.progress || 0);
        setCurrentProgressMessage(statusData.status_message || 'Processing...');
        
        if (statusData.status === 'completed') {
          clearInterval(interval);
          pollingIntervalRef.current = null;
          setIsPolling(false);
          setCurrentTaskId(null);
          setCurrentProgress(100);
          setCurrentProgressMessage('Complete!');
          
          console.log('🎯 [useAsyncPolling] Task completed, result:', statusData.result);
          onTaskComplete(statusData.result);
          
        } else if (statusData.status === 'failed') {
          clearInterval(interval);
          pollingIntervalRef.current = null;
          setIsPolling(false);
          setCurrentTaskId(null);
          
          const errorMessage = {
            id: messages.length + 1,
            type: 'agent',
            content: `I apologize, but I encountered an issue: ${statusData.error || 'Please try again.'}`,
            timestamp: new Date(),
            isError: true
          };
          setMessages(prev => [...prev, errorMessage]);
          
        } else if (statusData.status === 'timeout') {
          clearInterval(interval);
          pollingIntervalRef.current = null;
          setIsPolling(false);
          setCurrentTaskId(null);
          
          const timeoutMessage = {
            id: messages.length + 1,
            type: 'agent',
            content: `⏰ **Request Timed Out**\n\nYour request took longer than expected to process. This sometimes happens with complex analysis.\n\n**You can:**\n• Try asking a simpler question\n• Retry your original request\n• Ask me something else\n\nI'm ready to help! 🚀`,
            timestamp: new Date(),
            isSystemMessage: true
          };
          setMessages(prev => [...prev, timeoutMessage]);
        }
        
      } catch (error) {
        consecutiveErrorsRef.current += 1;
        console.warn(`[useAsyncPolling] Poll error (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_POLL_ERRORS}):`, error.message);
        
        if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
          clearInterval(interval);
          pollingIntervalRef.current = null;
          setIsPolling(false);
          setCurrentTaskId(null);
          
          const errorMessage = {
            id: messages.length + 1,
            type: 'agent',
            content: 'I\'m having trouble connecting to the server. Please try again in a moment.',
            timestamp: new Date(),
            isError: true
          };
          setMessages(prev => [...prev, errorMessage]);
        }
      }
    }, 2000);
    
    pollingIntervalRef.current = interval;
  }, [pollTaskStatus, messages.length, setMessages, onTaskComplete]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    setIsPolling(false);
    setCurrentTaskId(null);
    setCurrentProgress(0);
    setCurrentProgressMessage('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  return {
    isPolling,
    currentProgress,
    currentProgressMessage,
    currentTaskId,
    startPolling,
    stopPolling
  };
};

export default useAsyncPolling;



